/**
 * Entry-domain helpers shared by the home and log screens.
 *
 * Mostly one question: which day does an entry belong to? For a feed or a
 * diaper that is just its timestamp. For sleep it is not — the night sleep you
 * care about at breakfast started at 23:10 *yesterday*, and a parent thinks of
 * it as last night, not as two half-nights either side of midnight.
 */

import { isSameDay, minutesBetween } from './time';
import type { Entry, EntryType, SleepEntry } from './types';

/**
 * True when `entry` should show up in `day`'s log.
 *
 * A sleep counts for any day it overlaps, so an unfinished nap started before
 * midnight stays visible (and closable) this morning. Everything else belongs
 * to the day it happened on.
 */
export function entryTouchesDay(entry: Entry, day: Date, now: Date = new Date()): boolean {
  if (isSameDay(entry.at, day)) return true;
  if (entry.type !== 'sleep') return false;
  const end = entry.endAt ?? now;
  // Overlaps if it starts before the day ends and ends after the day begins.
  return isSameDay(end, day) || (entry.at < day && end > day);
}

/** A nap with no end yet — 記録中. */
export function isOpenSleep(entry: Entry): entry is SleepEntry {
  return entry.type === 'sleep' && entry.endAt === null;
}

/**
 * Resolve a sleep's end against its start.
 *
 * The end's calendar day is explicit (the sheets have a date field), with one
 * convenience kept from the time-only days: when both dates are the same day
 * and the end clock is at or before the start clock, the end rolls to the
 * next morning — the 23:40→06:20 case, which is exactly when nobody is doing
 * date arithmetic in their head. A result that is still not after the start
 * is invalid (null), which the sheets surface as a warning instead of saving
 * a negative-duration sleep.
 */
export function resolveSleepEnd(start: Date, end: Date): Date | null {
  const out = new Date(end);
  if (isSameDay(out, start) && out.getTime() <= start.getTime()) {
    out.setDate(out.getDate() + 1);
  }
  return out.getTime() > start.getTime() ? out : null;
}

/**
 * Total sleep across the given entries, in minutes. An open nap counts up to
 * now, which is what makes the running total on the home screen move.
 */
export function totalSleepMinutes(entries: Entry[], now: Date = new Date()): number {
  return entries
    .filter((e): e is SleepEntry => e.type === 'sleep')
    .reduce((total, s) => total + minutesBetween(s.at, s.endAt ?? now), 0);
}

/** Which entry types each log tab shows. 離乳食 sits with feeds — it is a meal. */
export const TAB_TYPES: Record<'feed' | 'diaper' | 'sleep', EntryType[]> = {
  feed: ['feed', 'solid'],
  diaper: ['diaper'],
  sleep: ['sleep'],
};
